import PropTypes from 'prop-types'
import { isNil } from 'ramda'
import ActiveRecord from 'model/activeRecord'
import BenefitTier from 'model/insurancePlans/benefitTier'
import PlanBenefitCategory from 'model/insurancePlans/planBenefitCategory/planBenefitCategory'
import { initArray } from 'model/helpers'
import { getNodes } from 'utils/paginationHelper'
import { PlanCategoryType } from 'generated/types'

// Benefits CMS, not to be mistaken with model/plans/plan

class InsurancePlan extends ActiveRecord {
  // named after the GraphQL scheme
  constructor(init) {
    super(init)
    if (isNil(init)) return undefined
    this._assets = getNodes(['assets'])(init)
    this._insurance_tiers = init.insurance_tiers
    this._carrier = init.carrier
    this._carrier_display_name = init.carrier_display_name
    this._carrier_phone_numbers = init.carrier_phone_numbers
    this._default_insurance_tier = init.default_insurance_tier
    this._docusign_uuid = init.docusign_uuid
    this._hipaa_auth = init.hipaa_auth
    this._image = init.image
    this._insurance_type = init.insurance_type
    this._insurance_info = init.insurance_info
    this._name = init.name
    this._network = init.network
    this._display_name = init.display_name
    this._start_date = init.start_date
    this._end_date = init.end_date
    this._external_references = init.external_references
    this._status = init.status
    this._shared_organizations = getNodes(['shared_organizations'])(init)
    this._category = init.category
    this._links = getNodes(['links'])(init)
    this._contacts = getNodes(['contacts'])(init)
    this._benefit_categories = initArray(
      init,
      ['benefit_categories', 'nodes'],
      PlanBenefitCategory
    )
    this._contacts_internal_notes = init.contacts_internal_notes
    this._contacts_external_notes = init.contacts_external_notes
    this._links_internal_notes = init.links_internal_notes
    this._links_external_notes = init.links_external_notes
    this._matching_method = init.matching_method
    this._notes = getNodes(['notes'])(init)
    this._carrier_phone_number = init.carrier_phone_number
    this._internal_notes_preview = init.internal_notes_preview
    this._monthly_premium = init.monthly_premium
  }

  static shape = {
    id: PropTypes.string.isRequired,
    benefit_tiers: PropTypes.arrayOf(PropTypes.instanceOf(BenefitTier)),
    benefit_categories: PropTypes.arrayOf(
      PropTypes.instanceOf(PlanBenefitCategory)
    ),
    carrier: PropTypes.string,
    carrier_display_name: PropTypes.string,
    carrier_phone_numbers: PropTypes.array,
    default_benefit_tier: PropTypes.object,
    docusign_uuid: PropTypes.string,
    hipaa_auth: PropTypes.bool,
    image: PropTypes.string,
    insurance_type: PropTypes.string,
    name: PropTypes.string,
    display_name: PropTypes.string,
    start_date: PropTypes.string,
    network: PropTypes.string,
    end_date: PropTypes.string,
    external_references: PropTypes.array,
    status: PropTypes.string,
    shared_organizations: PropTypes.array,
    category: PropTypes.object,
    links: PropTypes.array,
    contacts: PropTypes.array,
    contacts_external_notes: PropTypes.string,
    contacts_internal_notes: PropTypes.string,
    links_external_notes: PropTypes.string,
    links_internal_notes: PropTypes.string,
    matching_method: PropTypes.string,
    notes: PropTypes.array,
    insurance_tiers: PropTypes.array,
    assets: PropTypes.array,
    default_insurance_tier: PropTypes.object,
    carrier_phone_number: PropTypes.string,
    internal_notes_preview: PropTypes.array,
    monthly_premium: PropTypes.number
  }

  // Getters for class fields
  get assets() {
    return this._assets || []
  }

  get insurance_tiers() {
    return this._insurance_tiers || []
  }

  get carrier() {
    return this._carrier || 'N/A'
  }

  get carrier_display_name() {
    return this._carrier_display_name || ''
  }

  get carrier_phone_numbers() {
    return this._carrier_phone_numbers || []
  }

  get contacts() {
    return this._contacts || []
  }

  get contacts_internal_notes() {
    return this._contacts_internal_notes
  }

  get contacts_external_notes() {
    return this._contacts_external_notes
  }

  get links_internal_notes() {
    return this._links_internal_notes
  }

  get links_external_notes() {
    return this._links_external_notes
  }

  get matching_method() {
    return this._matching_method
  }

  get default_insurance_tier() {
    return this._default_insurance_tier || {}
  }

  get docusign_uuid() {
    return this._docusign_uuid
  }

  get hipaa_auth() {
    return this._hipaa_auth
  }

  get network() {
    return this._network
  }

  get notes() {
    return this._notes
  }

  get image() {
    return this._image
  }

  get insurance_info() {
    return this._insurance_info
  }

  get insurance_type() {
    return this._insurance_type || 'other'
  }

  get links() {
    return this._links || []
  }

  get name() {
    return this._name
  }

  get display_name() {
    return this._display_name
  }

  get start_date() {
    return this._start_date
  }

  get end_date() {
    return this._end_date
  }

  get external_references() {
    return this._external_references || []
  }

  get status() {
    return this._status
  }

  get shared_organizations() {
    return this._shared_organizations || []
  }

  get category() {
    return this._category || {}
  }

  get benefit_categories() {
    return this._benefit_categories
  }

  get carrier_phone_number() {
    return this._carrier_phone_number || ''
  }

  get internal_notes_preview() {
    return this._internal_notes_preview || []
  }

  get monthly_premium() {
    return this._monthly_premium ?? null
  }

  // Calculable
  get isDental() {
    return this.category.code === PlanCategoryType.DentalCare
  }

  get isMedicalCare() {
    return this.category.code === PlanCategoryType.MedicalCare
  }

  get isMedicalBehavioral() {
    const { code } = this.category
    return (
      code === PlanCategoryType.MedicalCare ||
      code === PlanCategoryType.Behavioral ||
      code === PlanCategoryType.MedicalBehavioral
    )
  }

  get isPharmacy() {
    return this.category.code === PlanCategoryType.Pharmacy
  }

  get isVision() {
    return this.category.code === PlanCategoryType.VisionCare
  }

  get categoryType() {
    if (this.isPharmacy) return 'pharmacy'
    if (this.isVision) return 'vision'
    if (this.isDental) return 'dental'

    return 'medical'
  }
}

export default InsurancePlan
